import React, { Fragment, memo, useCallback, useContext, useMemo, useState } from 'react'
import { CancelSmallMinor, TickMinor } from '@shopify/polaris-icons'
import { MenuActionItem } from 'src/interfaces/ui'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { ActionButton } from 'src/components/shared/ActionButton'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { addS } from 'src/utils/baseUtils'
import { SensitiveActionWithPictureModal } from 'src/components/shared/SubmitActionWithPictureModal'
import NotFoundImage from 'src/assets/images/not-found.png'
import { SubcontractorRequestsTableContext } from '../SubcontractorRequestsTable.context'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { ApproveSubcontractorRequestsModal } from '../../approve-subcontractor-requests/ApproveSubcontractorRequestsModal'
import { DeclineSubcontractorRequests } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'

const SubcontractorRequestsBatchActionButtonComponent = () => {
  const dispatch = useDispatch()

  const { selectedSubcontractorRequestIds } = useContext(SubcontractorRequestsTableContext)
  const { reload } = useContext(ReloadWrapperContext)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isApproveSubcontractorRequestsOpen, setIsApproveSubcontractorRequestsOpen] =
    useState(false)
  const [isConfirmDeclineOpen, setIsConfirmDeclineOpen] = useState(false)

  const isOpen = Boolean(anchorEl)

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleOpenConfirmDecline = useCallback(() => {
    setIsConfirmDeclineOpen(true)
  }, [])

  const handleCloseConfirmDecline = useCallback(() => {
    setIsConfirmDeclineOpen(false)
  }, [])

  const handleOpenApproveSubcontractorRequests = useCallback(() => {
    setIsApproveSubcontractorRequestsOpen(true)
  }, [])

  const handleCloseApproveSubcontractorRequests = useCallback(() => {
    setIsApproveSubcontractorRequestsOpen(false)
  }, [])

  const actionItems: MenuActionItem[] = useMemo(() => {
    const res: MenuActionItem[] = []

    res.push({
      label: 'Approve',
      dataTestId: 'requests-main-batch-approve-btn',
      icon: <TickMinor width={20} height={20} />,
      onClick: () => {
        if (!selectedSubcontractorRequestIds.length) {
          return
        }

        handleOpenApproveSubcontractorRequests()
        handleClose()
      },
    })

    res.push({
      label: 'Decline',
      icon: <CancelSmallMinor width={20} height={20} />,
      onClick: () => {
        if (!selectedSubcontractorRequestIds.length) {
          return
        }

        handleOpenConfirmDecline()
        handleClose()
      },
    })

    return res
  }, [selectedSubcontractorRequestIds])

  const handleDeclineSubcontractorRequests = useCallback(async () => {
    // TODO: need to test with real requests
    try {
      await createAsyncAction(
        dispatch,
        DeclineSubcontractorRequests.request({
          selectedSubcontractorRequestIds,
        }),
      )

      reload()

      showToast(
        `${addS(
          selectedSubcontractorRequestIds.length,
          'contractor',
        )} declined. Ask the master contractor to link them again if needed.`,
      )
    } catch (err) {
      handleApiError(err)
    }

    handleCloseConfirmDecline()
  }, [selectedSubcontractorRequestIds, reload])

  return (
    <Fragment>
      <ActionButton
        className={tw`px-4`}
        selected={!!anchorEl}
        color="white"
        text="Actions"
        disabled={!selectedSubcontractorRequestIds.length}
        onClick={handleClick}
        data-testid="requests-main-batch-actions-menu-btn"
      />
      <ActionMenu
        anchorEl={anchorEl}
        itemClassName={tw`w-[200px]`}
        isOpen={isOpen}
        items={actionItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
      />
      <SensitiveActionWithPictureModal
        isOpen={isConfirmDeclineOpen}
        title={`Are you sure you want to decline this ${addS(
          selectedSubcontractorRequestIds.length,
          'contractor',
        )}?`}
        text="Declining a contractor will remove them from the table. If you want to approve this contractor later, ask the master contractor to link them to you again."
        submitLabel="Decline"
        onClose={handleCloseConfirmDecline}
        onSubmit={handleDeclineSubcontractorRequests}
        imageSrc={NotFoundImage}
      />
      <ApproveSubcontractorRequestsModal
        isOpen={isApproveSubcontractorRequestsOpen}
        onClose={handleCloseApproveSubcontractorRequests}
        onSubmit={handleCloseApproveSubcontractorRequests}
        subcontractorRequestsSelection={selectedSubcontractorRequestIds}
      />
    </Fragment>
  )
}

export const SubcontractorRequestsBatchActionButton = memo(
  SubcontractorRequestsBatchActionButtonComponent,
)
